import { ApiError } from './api.ts';
import { el, karte, hinweis, clear, formatDate } from './ui.ts';
import {
  ladeEigeneVertrauenspersonen,
  ladeVertrauensBeziehungenAlsPerson,
  ladeVertrauensperson,
  widerrufeVertrauensperson,
  bestaetigeVertrauensperson,
  notfallzugriffAnfordern,
  ladeDokumenteAlsVertrauensperson,
  type TrusteeRow,
  type TrusteeBeziehung,
} from './vertrauensperson.ts';
import { downloadDocument, openDocument, type DocumentMeta, type OpenedDocument } from './documents.ts';

/**
 * Oberfläche für Vertrauensperson / Notfallzugang (Entscheidung 10.08.2026).
 *
 * In eine eigene Datei ausgelagert statt in main.ts, das bereits sehr groß
 * ist. Zwei getrennte Rollen, die dasselbe Kundenkonto gleichzeitig haben
 * kann: eigene Vertrauenspersonen benennen (`vertrauenspersonenKarte`) und
 * selbst als Vertrauensperson für jemand anderen auftreten
 * (`alsVertrauenspersonKarte`) — beides läuft über ein einziges Konto, ohne
 * Rollenwechsel oder zweiten Login.
 */

function fehlertext(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}

const STATUS_TEXT: Record<TrusteeRow['status'], string> = {
  eingeladen: 'Eingeladen – wartet auf Bestätigung',
  bestaetigt: 'Bestätigt',
  notfall_angefordert: 'Notfallzugriff angefordert',
  freigegeben: 'Freigeschaltet',
  widerrufen: 'Widerrufen',
};

// ---------------------------------------------------------------------------
// Eigene Vertrauenspersonen benennen und verwalten
// ---------------------------------------------------------------------------

export function vertrauenspersonenKarte(): HTMLElement {
  const status = el('div', { id: 'vp-status' });
  const liste = el('div', { id: 'vp-liste' });
  void ladeVertrauenspersonenListe(liste);

  return karte(
    el('h2', {}, 'Vertrauensperson für den Notfall'),
    el(
      'p',
      {},
      'Legen Sie eine Person fest, die im Notfall lesenden Zugriff auf Ihre Dokumente ' +
        'bekommt. Erst wenn diese Person selbst Zugriff anfordert und die von Ihnen ' +
        'festgelegte Wartezeit ohne Widerspruch verstreicht, kann sie Ihre Dokumente ' +
        'einsehen — Sie werden über jede Anforderung sofort per Mail informiert.',
    ),
    hinweis(
      'Ist die Person bereits selbst Kundin bei uns, braucht sie kein zweites Konto ' +
        '— ihr bestehender Zugang gilt dafür automatisch mit.',
    ),
    el('label', { for: 'vp-email' }, 'E-Mail-Adresse'),
    el('input', { type: 'email', id: 'vp-email' }),
    el('label', { for: 'vp-name' }, 'Name'),
    el('input', { type: 'text', id: 'vp-name' }),
    el('label', { for: 'vp-wartezeit' }, 'Wartezeit bis zur Freischaltung (1–90 Tage)'),
    el('input', { type: 'number', id: 'vp-wartezeit', min: '1', max: '90', value: '14' }),
    status,
    el(
      'div',
      { class: 'knopfreihe' },
      el(
        'button',
        { id: 'vp-einladen', onclick: () => void vertrauenspersonEinladen(status, liste) },
        'Vertrauensperson hinterlegen',
      ),
    ),
    el('h3', {}, 'Hinterlegte Vertrauenspersonen'),
    liste,
  );
}

async function vertrauenspersonEinladen(status: HTMLElement, liste: HTMLElement): Promise<void> {
  const email = document.querySelector<HTMLInputElement>('#vp-email')!.value.trim();
  const name = document.querySelector<HTMLInputElement>('#vp-name')!.value.trim();
  const wartezeit = Number(document.querySelector<HTMLInputElement>('#vp-wartezeit')!.value);
  const button = document.querySelector<HTMLButtonElement>('#vp-einladen')!;

  clear(status);
  if (!email || !name) {
    status.append(hinweis('Bitte E-Mail-Adresse und Namen angeben.', 'warnung'));
    return;
  }
  if (!Number.isInteger(wartezeit) || wartezeit < 1 || wartezeit > 90) {
    status.append(hinweis('Die Wartezeit muss zwischen 1 und 90 Tagen liegen.', 'warnung'));
    return;
  }

  button.disabled = true;
  try {
    await ladeVertrauensperson(email, name, wartezeit);
    document.querySelector<HTMLInputElement>('#vp-email')!.value = '';
    document.querySelector<HTMLInputElement>('#vp-name')!.value = '';
    clear(status);
    status.append(hinweis('Vertrauensperson hinterlegt. Die Einladung ist per Mail unterwegs.', 'erfolg'));
    await ladeVertrauenspersonenListe(liste);
  } catch (error) {
    clear(status);
    status.append(hinweis(fehlertext(error), 'warnung'));
  } finally {
    button.disabled = false;
  }
}

async function ladeVertrauenspersonenListe(container: HTMLElement): Promise<void> {
  clear(container);
  const trustees = await ladeEigeneVertrauenspersonen();

  if (trustees.length === 0) {
    container.append(el('p', { class: 'leise' }, 'Noch keine Vertrauensperson hinterlegt.'));
    return;
  }

  container.append(
    el(
      'ul',
      { class: 'dokumentliste' },
      ...trustees.map((t) =>
        el(
          'li',
          {},
          el(
            'div',
            {},
            el('div', { class: 'dokumentname' }, `${t.display_name} (${t.email})`),
            el(
              'div',
              { class: 'leise' },
              `${STATUS_TEXT[t.status]} · Wartezeit ${t.wartezeit_tage} Tag(e)` +
                (t.status === 'notfall_angefordert' && t.notfall_freizuschalten_ab
                  ? ` · Freischaltung ab ${formatDate(t.notfall_freizuschalten_ab)}, falls Sie nicht widersprechen`
                  : ''),
            ),
          ),
          el(
            'div',
            { class: 'knopfreihe' },
            t.status === 'eingeladen' && t.schluessel_bereit
              ? el(
                  'button',
                  { onclick: () => void vertrauenspersonBestaetigen(t.id, container) },
                  'Bestätigen',
                )
              : null,
            t.status === 'eingeladen' && !t.schluessel_bereit
              ? el('span', { class: 'leise' }, 'Wartet auf Passwort-Einrichtung der Person')
              : null,
            t.status !== 'widerrufen'
              ? el(
                  'button',
                  { class: 'warnung', onclick: () => void vertrauenspersonWiderrufen(t.id, container) },
                  'Widerrufen',
                )
              : null,
          ),
        ),
      ),
    ),
  );
}

async function vertrauenspersonBestaetigen(id: string, container: HTMLElement): Promise<void> {
  try {
    await bestaetigeVertrauensperson(id);
    await ladeVertrauenspersonenListe(container);
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

async function vertrauenspersonWiderrufen(id: string, container: HTMLElement): Promise<void> {
  if (
    !window.confirm('Diese Vertrauensperson widerrufen? Der Zugriff endet damit sofort und endgültig.')
  ) {
    return;
  }
  try {
    await widerrufeVertrauensperson(id);
    await ladeVertrauenspersonenListe(container);
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

// ---------------------------------------------------------------------------
// Selbst als Vertrauensperson für jemand anderen
// ---------------------------------------------------------------------------

export function alsVertrauenspersonKarte(): HTMLElement {
  const container = el('div', { id: 'vp-als-person' });
  void ladeAlsPersonListe(container);
  return karte(
    el('h2', {}, 'Ihre Rolle als Vertrauensperson'),
    el(
      'p',
      { class: 'leise' },
      'Personen, die Sie als Vertrauensperson für den Notfall benannt haben.',
    ),
    container,
  );
}

async function ladeAlsPersonListe(container: HTMLElement): Promise<void> {
  clear(container);
  const beziehungen = await ladeVertrauensBeziehungenAlsPerson();

  if (beziehungen.length === 0) {
    container.append(el('p', { class: 'leise' }, 'Niemand hat Sie bisher als Vertrauensperson benannt.'));
    return;
  }

  container.append(...beziehungen.map((b) => beziehungsKarte(b, container)));
}

function beziehungsKarte(b: TrusteeBeziehung, listContainer: HTMLElement): HTMLElement {
  const dokumente = el('div');
  if (b.status === 'freigegeben') void zeigeDokumente(b.id, dokumente);

  return el(
    'div',
    { class: 'karte', style: 'margin-top:1rem' },
    el('h3', {}, b.customer_name),
    el(
      'p',
      { class: 'leise' },
      `${STATUS_TEXT[b.status]} · Wartezeit ${b.wartezeit_tage} Tag(e)` +
        (b.status === 'notfall_angefordert' && b.notfall_freizuschalten_ab
          ? ` · Freischaltung ab ${formatDate(b.notfall_freizuschalten_ab)}, falls kein Widerspruch erfolgt`
          : ''),
    ),
    b.status === 'bestaetigt'
      ? el(
          'button',
          { onclick: () => void notfallAnfordernKlick(b.id, listContainer) },
          'Notfallzugriff anfordern',
        )
      : null,
    b.status === 'freigegeben' ? dokumente : null,
  );
}

async function notfallAnfordernKlick(id: string, listContainer: HTMLElement): Promise<void> {
  if (
    !window.confirm(
      'Notfallzugriff anfordern? Die betroffene Person wird sofort per Mail benachrichtigt ' +
        'und kann innerhalb der vereinbarten Wartezeit widersprechen.',
    )
  ) {
    return;
  }
  try {
    await notfallzugriffAnfordern(id);
    await ladeAlsPersonListe(listContainer);
  } catch (error) {
    window.alert(fehlertext(error));
  }
}

async function zeigeDokumente(customerTrusteeId: string, container: HTMLElement): Promise<void> {
  clear(container);
  container.append(el('p', { class: 'leise' }, 'Dokumente werden geladen …'));

  let metas: DocumentMeta[];
  try {
    metas = await ladeDokumenteAlsVertrauensperson(customerTrusteeId);
  } catch (error) {
    clear(container);
    container.append(hinweis(fehlertext(error), 'warnung'));
    return;
  }

  const geoeffnet: OpenedDocument[] = [];
  for (const meta of metas) {
    try {
      geoeffnet.push(await openDocument(meta));
    } catch {
      // Ein einzelnes nicht entschlüsselbares Dokument blockiert die Liste nicht.
    }
  }

  clear(container);
  if (geoeffnet.length === 0) {
    container.append(el('p', { class: 'leise' }, 'Keine Dokumente vorhanden.'));
    return;
  }
  container.append(
    el(
      'ul',
      { class: 'dokumentliste kompakt' },
      ...geoeffnet.map((doc) =>
        el(
          'li',
          {},
          el('span', { class: 'dokumentname' }, doc.filename),
          el('button', { onclick: () => void oeffnenAlsVertrauensperson(doc) }, 'Öffnen'),
        ),
      ),
    ),
  );
}

/** Wie `oeffnen()` in main.ts, nur ohne Zugriff auf dessen internen Zustand. */
async function oeffnenAlsVertrauensperson(document: OpenedDocument): Promise<void> {
  const fenster = window.open('', '_blank');
  if (!fenster) {
    window.alert(
      'Ihr Browser hat das Fenster blockiert. Bitte erlauben Sie Pop-ups für diese Seite ' +
        'und klicken Sie erneut auf „Öffnen".',
    );
    return;
  }
  fenster.opener = null;
  try {
    fenster.document.write(
      '<p style="font-family:system-ui;padding:2rem">Dokument wird entschlüsselt …</p>',
    );
  } catch {
    // Nur eine Zwischenanzeige – wenn sie nicht geht, geht der Rest trotzdem.
  }

  try {
    const blob = await downloadDocument(document);
    const url = URL.createObjectURL(blob);
    fenster.location.replace(url);
  } catch (error) {
    fenster.close();
    window.alert(fehlertext(error));
  }
}
